import { unwrap } from '@hive/api';
import { Select, Modal, ProblemBox, useLoad, useToast } from '@hive/ui';
import { useState } from 'react';
import { useI18nP, type PlatformKey } from '../i18n';
import { useSession } from './session';

export type TypeView = { key: string; label?: string | null; custom: boolean; groupId?: string | null; count: number };
export type GroupView = { id: string; name: string; sort: number; types: TypeView[] };
export type Catalog = { groups: GroupView[]; ungrouped: TypeView[] };

/** Rótulos próprios (tipos da empresa e nomes trocados dos internos), lidos pelo useValueLabel ('kind'). */
let labels: Record<string, string> = {};

export function customKindLabel(key: string): string | undefined {
  return labels[key];
}

/** Catálogo de tipos e agrupamentos; carregar atualiza os rótulos próprios para toda a console. */
export function useAssetTypes() {
  const s = useSession();
  const [tick, setTick] = useState(0);
  const v = useLoad(
    () =>
      (unwrap(s.api.GET('/api/v1/asset-types')) as unknown as Promise<Catalog>).then((c) => {
        labels = Object.fromEntries([...c.groups.flatMap((g) => g.types), ...c.ungrouped].filter((x) => x.label).map((x) => [x.key, x.label as string]));
        return c;
      }),
    [s.api, tick],
  );
  return { ...v, reload: () => setTick((n) => n + 1) };
}

/** Nome do tipo: o próprio da empresa, senão o traduzido, senão a chave. */
export function useKindName() {
  const { t, has } = useI18nP();
  return (key: string | null | undefined) => {
    if (!key) return '—';
    const own = labels[key];
    if (own) return own;
    return has(`kind.${key}`) ? t(`kind.${key}` as PlatformKey) : key;
  };
}

/** Seletor de tipo agrupado (optgroup por agrupamento); com groups, cada grupo inteiro também é uma opção (filtro). */
export function KindOptions({ catalog, groups }: { catalog: Catalog | undefined; groups?: boolean }) {
  const { t } = useI18nP();
  const name = useKindName();
  if (!catalog) return null;
  return (
    <>
      {catalog.groups.map((g) => (
        <optgroup key={g.id} label={g.name}>
          {groups && <option value={`group:${g.id}`}>{t('types.wholeGroup', { name: g.name })}</option>}
          {g.types.map((x) => (
            <option key={x.key} value={x.key}>
              {name(x.key)}
            </option>
          ))}
        </optgroup>
      ))}
      {catalog.ungrouped.length > 0 && (
        <optgroup label={t('types.ungrouped')}>
          {catalog.ungrouped.map((x) => (
            <option key={x.key} value={x.key}>
              {name(x.key)}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

/** Trocar o tipo de um ou mais ativos; "Automático" devolve a decisão ao inventário e à descoberta. */
export function ChangeKindModal({ assets, current, manual, onClose }: { assets: string[]; current?: string | null; manual?: boolean; onClose: (changed: boolean) => void }) {
  const { t } = useI18nP();
  const s = useSession();
  const toast = useToast();
  const types = useAssetTypes();
  const [kind, setKind] = useState(manual ? (current ?? '') : '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await unwrap(s.api.POST('/api/v1/assets/kind', { body: { assets, kind } }));
      toast(t(kind === 'auto' ? 'types.autoDone' : 'types.changed', { count: assets.length }), { kind: 'ok' });
      onClose(true);
    } catch (e) {
      toast(<ProblemBox error={e} />, { kind: 'crit', timeout: 9000 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('types.changeTitle', { count: assets.length })}
      onClose={() => onClose(false)}
      foot={
        <button type="button" className="btn btn-primary" disabled={!kind || busy} onClick={() => void save()}>
          {t('types.apply')}
        </button>
      }
    >
      <div className="stack">
        <p className="small">{t('types.changeLead')}</p>
        <div className="field">
          <label htmlFor="ck-kind">{t('asset.f.kind')}</label>
          <Select id="ck-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">{t('types.choose')}</option>
            <option value="auto">{t('types.auto')}</option>
            <KindOptions catalog={types.data} />
          </Select>
        </div>
        {manual && <p className="xs muted">{t('types.isManual')}</p>}
      </div>
    </Modal>
  );
}
